<template>
  <div>
    <div>
      <div class="page-bar">
        <breadcrumb :crumbs="breadcrumbArray" />
      </div>
      <h1 class="page-title">
        PIN
      </h1>
      <div class="note note-info">
        <p>Your PIN is used to secure a POS device</p>
      </div>

      <div
        v-if="can()"
        class="portlet box blue-hoki margin-top-20"
      >
        <div
          class="portlet-title bg-blue-chambray"
        >
          <div class="menu-image-main">
            <img src="@/../public/client_logo.png">
          </div>
          <div class="caption">
            <span class="caption-subject font-default bold uppercase">
              PIN
            </span>
            <div class="caption-desc font-grey-cascade">
              Set or delete your PIN
            </div>
          </div>
        </div>
        <div
          class="portlet-body"
        >
          <div class="row">
            <div class="col-md-12">
              <div
                v-show="viewErrorMessage"
                ref="viewErrorMessage"
                class="alert alert-danger"
              >
                <button
                  class="close"
                  @click.prevent="clearError('viewErrorMessage')"
                />
                <span>{{ viewErrorMessage }}</span>
              </div>
            </div>
            <loading-screen
              v-if="loading"
              :show="loading"
              :color="'#2C3E50'"
              :display="'inline'"
            />
            <template v-else>
              <div
                v-if="toView.pin.length"
                class="col-md-6"
              >
                <div
                  class="form-group form-md-line-input form-md-floating-label"
                >
                  <div class="input-group">
                    <input
                      id="pin"
                      :type="inputType.toView"
                      name="existing-PIN"
                      :value="toView.pin"
                      readonly
                      class="form-control input-sm"
                      :class="{'edited': toView.pin.length}"
                    >
                    <label for="existing-PIN">
                      PIN
                    </label>
                    <span
                      class="input-group-addon clickable"
                      @click="flipMask(toView)"
                    >
                      <i
                        class="fa"
                        :class="{'fa-eye' : toView.masked, 'fa-eye-slash' : !toView.masked}"
                      />
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  class="btn blue pull-right"
                  @click="openEditModal()"
                >
                  Edit
                </button>
                <button
                  type="submit"
                  class="btn btn-outline blue pull-right mr-1em"
                  :disabled="deleting"
                  @click="openDeleteModal"
                >
                  Delete
                </button>
              </div>
              <div
                v-else
                class="col-md-6"
              >
                <form
                  @submit.prevent="upsertPIN()"
                  data-vv-scope="create"
                >
                  <div
                    class="form-group form-md-line-input form-md-floating-label"
                    :class="{'has-error' : errors.has('create.PIN')}"
                  >
                    <div class="input-group">
                      <input
                        id="pin"
                        v-validate="'required|numeric|min:4|max:11'"
                        :type="inputType.toCreate"
                        name="PIN"
                        v-model="toCreate.pin"
                        class="form-control input-sm"
                        :class="{'edited': toCreate.pin.length}"
                      >
                      <label for="pin">
                        PIN
                      </label>
                      <span
                        class="input-group-addon clickable"
                        @click="flipMask(toCreate)"
                      >
                        <i
                          class="fa"
                          :class="{'fa-eye' : toCreate.masked, 'fa-eye-slash' : !toCreate.masked}"
                        />
                      </span>
                    </div>
                    <span class="help-block persist">
                      {{ errors.first('create.PIN') }}
                    </span>
                  </div>
                  <div
                    class="form-group form-md-line-input form-md-floating-label"
                    :class="{'has-error' : errors.has('create.confirm-PIN')}"
                  >
                    <div class="input-group">
                      <input
                        id="confirm-PIN"
                        v-validate="{ required: true, is: toCreate.pin }"
                        :type="inputType.toCreate"
                        name="confirm-PIN"
                        v-model="toCreate.confirm"
                        class="form-control input-sm"
                        :class="{'edited': toCreate.confirm.length}"
                      >
                      <label for="confirm-PIN">
                        Confirm PIN
                      </label>
                      <span
                        class="input-group-addon clickable"
                        @click="flipMask(toCreate)"
                      >
                        <i
                          class="fa"
                          :class="{'fa-eye' : toCreate.masked, 'fa-eye-slash' : !toCreate.masked}"
                        />
                      </span>
                    </div>
                    <span class="help-block persist">
                      {{ errors.first('create.confirm-PIN') }}
                    </span>
                  </div>
                  <button
                    type="submit"
                    class="btn blue pull-right"
                    :disabled="errors.items.length || updating"
                  >
                    Create
                    <i
                      v-show="updating"
                      class="fa fa-spinner fa-pulse fa-fw"
                    />
                  </button>
                </form>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- EDIT MODAL START -->
    <modal
      ref="editModal"
      :show="showEditModal"
      effect="fade"
      @closeOnEscape="closeEditModal"
    >
      <div
        slot="modal-header"
        class="modal-header center"
      >
        <button
          type="button"
          class="close"
          @click="closeEditModal()"
        >
          <span>&times;</span>
        </button>
        <h4 class="modal-title center">
          Update PIN
        </h4>
      </div>
      <div
        slot="modal-body"
        class="modal-body"
      >
        <div
          v-show="editErrorMessage"
          ref="editErrorMessage"
          class="alert alert-danger"
        >
          <button
            class="close"
            @click.prevent="clearError('editErrorMessage')"
          />
          <span>{{ editErrorMessage }}</span>
        </div>
        <form
          @submit.prevent="upsertPIN()"
          data-vv-scope="update"
        >
          <div
            class="form-group form-md-line-input form-md-floating-label"
            :class="{'has-error' : errors.has('update.PIN')}"
          >
            <div class="input-group">
              <input
                v-model="toEdit.pin"
                id="update-PIN"
                v-validate="'required|numeric|min:4|max:11'"
                :type="inputType.toEdit"
                name="PIN"
                class="form-control input-sm"
                :class="{'edited': toEdit.pin.length}"
              >
              <label for="update-PIN">
                New PIN
              </label>
              <span
                class="input-group-addon clickable"
                @click="flipMask(toEdit)"
              >
                <i
                  class="fa"
                  :class="{'fa-eye' : toEdit.masked, 'fa-eye-slash' : !toEdit.masked}"
                />
              </span>
            </div>
            <span class="help-block persist">
              {{ errors.first('update.PIN') }}
            </span>
          </div>
          <div
            class="form-group form-md-line-input form-md-floating-label"
            :class="{'has-error' : errors.has('update.confirm-PIN')}"
          >
            <div class="input-group">
              <input
                id="update-confirm-PIN"
                v-validate="{ required: true, is: toEdit.pin }"
                :type="inputType.toEdit"
                name="confirm-PIN"
                v-model="toEdit.confirm"
                class="form-control input-sm"
                :class="{'edited': toEdit.confirm.length}"
              >
              <label for="update-confirm-PIN">
                Confirm PIN
              </label>
              <span
                class="input-group-addon clickable"
                @click="flipMask(toEdit)"
              >
                <i
                  class="fa"
                  :class="{'fa-eye' : toEdit.masked, 'fa-eye-slash' : !toEdit.masked}"
                />
              </span>
            </div>
            <span class="help-block persist">
              {{ errors.first('update.confirm-PIN') }}
            </span>
          </div>
        </form>
      </div>
      <div
        slot="modal-footer"
        class="modal-footer"
      >
        <div class="row">
          <div class="col-xs-12">
            <button
              type="submit"
              class="btn blue"
              :disabled="errors.items.length || updating"
              @click.stop="upsertPIN()"
            >
              Save
              <i
                v-show="updating"
                class="fa fa-spinner fa-pulse fa-fw"
              />
            </button>
          </div>
        </div>
      </div>
    </modal>
    <!-- EDIT MODAL END -->

    <!-- DELETE MODAL START -->
    <modal
      ref="deleteModal"
      :show="showDeleteModal"
      effect="fade"
      @closeOnEscape="closeDeleteModal"
    >
      <div
        slot="modal-header"
        class="modal-header center"
      >
        <button
          type="button"
          class="close"
          @click="closeDeleteModal()"
        >
          <span>&times;</span>
        </button>
        <h4 class="modal-title center">
          Delete PIN
        </h4>
      </div>
      <div
        slot="modal-body"
        class="modal-body"
      >
        <div
          v-show="deleteErrorMessage"
          ref="deleteErrorMessage"
          class="alert alert-danger"
        >
          <button
            class="close"
            @click.prevent="clearError('deleteErrorMessage')"
          />
          <span>{{ deleteErrorMessage }}</span>
        </div>
        <p>Are you sure you want to delete your PIN?</p>
      </div>
      <div
        slot="modal-footer"
        class="modal-footer"
      >
        <div class="row">
          <div class="col-xs-12">
            <button
              type="button"
              class="btn blue"
              @click.stop="deletePIN()"
            >
              Delete
              <i
                v-show="deleting"
                class="fa fa-spinner fa-pulse fa-fw"
              />
            </button>
          </div>
        </div>
      </div>
    </modal>
    <!-- DELETE MODAL END -->
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Breadcrumb from '@/components/modules/Breadcrumb'
import LoadingScreen from '@/components/modules/LoadingScreen'
import Modal from '@/components/modules/Modal'
import AdminPINFunctions from '@/controllers/AdminPIN'
import ajaxErrorHandler from '@/controllers/ErrorController'

export default {
  name: 'AdminPIN',
  components: {
    Breadcrumb,
    LoadingScreen,
    Modal
  },
  data: () => ({
    breadcrumbArray: [
      { name: 'PIN', link: false }
    ],

    loading: false,
    viewErrorMessage: '',
    toView: {
      pin: '',
      masked: true
    },

    toCreate: {
      pin: '',
      confirm: '',
      masked: true
    },

    showEditModal: false,
    updating: false,
    editErrorMessage: '',
    toEdit: {
      pin: '',
      confirm: '',
      masked: true
    },

    showDeleteModal: false,
    deleting: false,
    deleteErrorMessage: ''
  }),
  computed: {
    inputType () {
      let types = {
        toView: this.toView.masked ? 'password' : 'text',
        toCreate: this.toCreate.masked ? 'password' : 'text',
        toEdit: this.toEdit.masked ? 'password' : 'text'
      }
      return types
    },
    ...mapGetters(['can'])
  },
  created () {
    this.getPIN()
  },
  methods: {
    /**
     * To clear an error
     * @function
     * @param {object} name - Name of the error variable
     * @returns {undefined}
     */
    clearError (name) {
      this[name] = ''
    },
    /**
     * To switch between text and password field types
     * @function
     * @param {object} object - The object to modify
     * @returns {undefined}
     */
    flipMask (object) {
      object.masked = !object.masked
    },
    /**
     * To fetch the existing PIN from the API
     * @function
     * @returns {undefined}
     */
    getPIN () {
      this.loading = true
      return AdminPINFunctions.getAdminPIN(this.$root.activeUser.id)
        .then(response => {
          this.toView.pin = response.payload.pin.toString()
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorText: 'We could not fetch your PIN',
            errorName: 'viewErrorMessage',
            vue: _this
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    /**
     * To open the edit modal
     * @function
     * @returns {undefined}
     */
    openEditModal () {
      this.showEditModal = true
    },
    /**
     * To save the PIN in the database
     * @function
     * @returns {undefined}
     */
    upsertPIN () {
      const scope = this.toView.pin ? 'update.*' : 'create.*'
      const pin = this.toView.pin ? this.toEdit.pin : this.toCreate.pin
      return this.$validator.validate(scope)
        .then(valid => {
          if (!valid) return

          this.updating = true
          return AdminPINFunctions.upsertAdminPIN({pin})
            .then(response => {
              this.toView.pin = response.payload.pin.toString()
              this.showUpsertSuccess()
              this.closeEditModal()
              this.resetEdit()
            })
            .catch(reason => {
              ajaxErrorHandler({
                reason,
                errorText: 'We could not save the PIN',
                errorName: 'editErrorMessage',
                vue: _this,
                containerRef: 'editModal'
              })
            })
            .finally(() => {
              this.updating = false
            })
        })
    },
    /**
     * To reset the create and edit forms
     * @function
     * @returns {undefined}
     */
    resetEdit () {
      this.toCreate.pin = ''
      this.toCreate.confirm = ''
      this.toEdit.pin = ''
      this.toEdit.confirm = ''
      this.$validator.reset()
    },
    /**
     * To notify user of the outcome of the call
     * @function
     * @param {object} payload - The payload object from the server response
     * @returns {undefined}
     */
    showUpsertSuccess (payload = {}) {
      let title = 'Success'
      let text = 'The PIN has been saved'
      let type = 'success'

      if (payload && payload.pending_approval) {
        title = 'Approval Required'
        text = 'The change has been sent for approval'
        type = 'info'
      }

      this.$swal({
        title,
        text,
        type
      })
    },
    /**
     * To close the edit modal
     * @function
     * @returns {undefined}
     */
    closeEditModal () {
      this.showEditModal = false
      this.resetEdit()
    },
    /**
     * To open the delete modal
     * @function
     * @returns {undefined}
     */
    openDeleteModal () {
      this.showDeleteModal = true
    },
    /**
     * To remove the PIN from the database
     * @function
     * @returns {undefined}
     */
    deletePIN () {
      const _this = this
      this.deleting = true
      return AdminPINFunctions.deleteAdminPIN()
        .then(response => {
          this.toView.pin = ''
          this.closeDeleteModal()
          this.showDeleteSuccess()
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorText: 'We could not delete the PIN',
            errorName: 'deleteErrorMessage',
            vue: _this,
            containerRef: 'deleteModal'
          })
        })
        .finally(() => {
          this.deleting = false
        })
    },
    /**
     * To close the edit modal
     * @function
     * @returns {undefined}
     */
    closeDeleteModal () {
      this.showDeleteModal = false
    },
    /**
     * To notify user of the outcome of the call
     * @function
     * @param {object} payload - The payload object from the server response
     * @returns {undefined}
     */
    showDeleteSuccess (payload = {}) {
      let title = 'Success'
      let text = 'The PIN has been removed'
      let type = 'success'

      if (payload && payload.pending_approval) {
        title = 'Approval Required'
        text = 'The removal has been sent for approval'
        type = 'info'
      }

      this.$swal({
        title,
        text,
        type
      })
    }
  }
}
</script>
